/* ==========================================================================
   ENDURRAD — Ricerca predittiva sulle barre custom
   File: assets/endurrad-predictive.js
   --------------------------------------------------------------------------
   Estende la classe PredictiveSearch nativa del tema Concept 6.0.0 senza
   modificare assets/search.js (upgrade-safe).

   Cosa sovrascrive e perché:

   1. dropdownParent  -> il getter originale cerca solo .collection o
                         .header-search, quindi sulle barre custom onFocus()
                         usciva subito e il pannello non si apriva mai.

   2. buildUrl()      -> l'originale usa `theme.routes.shop_url`, che punta al
                         dominio primario: su endurrad.fr/.de/.es la fetch
                         sarebbe partita verso endurrad.com (CORS + market
                         sbagliato). Qui si usa window.location.origin, così
                         ogni dominio interroga sé stesso. Il prefisso locale
                         arriva da routes.predictive_search_url via Liquid.
                         Elimina anche la dipendenza da theme.utils.sectionId().

   3. renderSearchResults() -> l'originale fa document.getElementById(), quindi
                         con più barre nella stessa pagina solo la prima si
                         aggiornerebbe. Qui il target è interno al form, quindi
                         le istanze sono illimitate e indipendenti.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ID = 'endurrad-predictive';
  var RESULTS_ID = 'PredictiveSearchResults-' + SECTION_ID;
  var MAX_LIMIT = 10; // limite massimo imposto da Shopify su resources[limit]

  function define() {
    var PredictiveSearch = customElements.get('predictive-search');

    if (!PredictiveSearch) return;
    if (customElements.get('endurrad-predictive-search')) return;

    class EndurradPredictiveSearch extends PredictiveSearch {
      connectedCallback() {
        super.connectedCallback();

        /* Chiusura al click esterno.
           L'overlay nativo del tema è `pointer-events:none` finché il CSS di
           Concept non lo attiva, e quel CSS è scoped su .header-search /
           .collection: sulle barre custom non farebbe mai match. Un listener
           sul documento è indipendente dal CSS del tema e quindi upgrade-safe.
           `pointerdown` anticipa il blur dell'input ed evita che il dropdown
           resti aperto per un frame. */
        this.on(document, 'pointerdown', this.onDocumentPointerdown.bind(this));
      }

      onDocumentPointerdown(event) {
        if (!document.body.classList.contains('predictive-search-open')) return;

        /* Click dentro il form (input, pulsanti, risultati): non chiudere.
           I link dei prodotti restano cliccabili e la navigazione prosegue. */
        if (this.contains(event.target)) return;

        this.close();
      }

      /* Ancora del dropdown: sempre presente attorno alle barre custom. */
      get dropdownParent() {
        return this.closest('.endurrad-search-wrapper');
      }

      /* Path locale-aware passato da Liquid (es. /search/suggest, /en/search/suggest) */
      get searchPath() {
        return this.getAttribute('data-predictive-url') || '/search/suggest';
      }

      get limit() {
        var n = parseInt(this.getAttribute('data-limit'), 10);
        if (!isFinite(n) || n < 1) n = 6;
        return Math.min(n, MAX_LIMIT);
      }

      get resultsTarget() {
        return this.querySelector('[data-endurrad-results]');
      }

      /* Campi in cui cercare.
         Attenzione: `resources[options][fields]` SOSTITUISCE il set di default
         (title, product_type, variants.title, vendor) invece di aggiungersi:
         vanno quindi elencati tutti, altrimenti si perdono ricerche che oggi
         funzionano. `title` copre anche collezioni, articoli e pagine.
         Personalizzabile da Liquid con data-search-fields. */
      get searchFields() {
        return this.getAttribute('data-search-fields') ||
          'title,product_type,variants.title,vendor,variants.sku';
      }

      buildUrl() {
        var url = new URL(this.searchPath, window.location.origin);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.limit);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][fields]', this.searchFields);
        url.searchParams.set('section_id', SECTION_ID);

        return url;
      }

      renderSearchResults(responseText) {
        var target = this.resultsTarget;
        if (!target) return;

        var parsed = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(RESULTS_ID);

        if (parsed) target.replaceChildren.apply(target, parsed.childNodes);
      }
    }

    customElements.define('endurrad-predictive-search', EndurradPredictiveSearch, {
      extends: 'form'
    });
  }

  if (customElements.get('predictive-search')) {
    define();
  } else {
    customElements.whenDefined('predictive-search').then(define);
  }
})();
